import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Flex,
  Input,
  Popconfirm,
  Progress,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DownloadOutlined,
  MailOutlined,
  ReloadOutlined,
  RedoOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import Table from "components/Common/Table";
import SendTemplateDrawer from "components/Leads/SendTemplateDrawer";
import { useNotification } from "NotificationContext";
import * as api from "api/activityTemplates";

const { Text } = Typography;

/**
 * Bulk sends from the leads / accounts list.
 *
 * Each "Send to N" in the bulk composer becomes one batch, prepared and sent
 * by the server. This tab lists them with live progress; opening one shows
 * every message, why the failed ones failed, and lets them be retried.
 *
 * Failure kinds (set by the backend, activitytemplate/failures.py) decide what
 * is worth retrying - see FAILURE_KINDS below.
 */

// While anything is still moving, poll this often.
const POLL_MS = 5000;

export const FAILURE_KINDS = {
  TEMPORARY: {
    label: "Temporary",
    color: "orange",
    help: "A timeout, rate limit or provider hiccup. Retried automatically up to 3 times; retry again if it still failed.",
  },
  SETUP: {
    label: "Setup",
    color: "purple",
    help: "Credentials, the template or the account needs fixing first (e.g. SMTP login, Meta token, template parameters). Fix it, then retry.",
  },
  PERMANENT: {
    label: "Bad address",
    color: "red",
    help: "The email address or number is invalid or unreachable. Correct it on the lead first; retrying as-is fails again and hurts the sender's reputation.",
  },
  WINDOW_CLOSED: {
    label: "Window closed",
    color: "gold",
    help: "Free-form WhatsApp outside the 24-hour reply window. Resend these with a Meta-approved template.",
  },
};

const STATE_TAGS = {
  queued: { color: "default", label: "Queued" },
  retrying: { color: "orange", label: "Retrying" },
  sent: { color: "green", label: "Sent" },
  failed: { color: "red", label: "Failed" },
};

const BATCH_STATUS = {
  PREPARING: { color: "blue", label: "Preparing" },
  SENDING: { color: "processing", label: "Sending" },
  COMPLETED: { color: "green", label: "Completed" },
};

const isActive = (batch) => batch && batch.status !== "COMPLETED";

const errorText = (err) => {
  const data = err?.response?.data;
  if (!data) return err?.message || "Unknown error";
  if (typeof data === "string") return data;
  return data.error || data.detail || Object.values(data).flat().join(" ");
};

const when = (value) => (value ? dayjs(value).format("D MMM YYYY, h:mm a") : "—");

const ChannelIcon = ({ channel }) =>
  channel === "WHATSAPP" ? (
    <WhatsAppOutlined style={{ color: "#25D366" }} />
  ) : (
    <MailOutlined style={{ color: "#1677ff" }} />
  );

const KindTag = ({ kind }) => {
  const info = FAILURE_KINDS[kind];
  if (!info) return null;
  return (
    <Tooltip title={info.help}>
      <Tag color={info.color} style={{ marginInlineEnd: 0 }}>
        {info.label}
      </Tag>
    </Tooltip>
  );
};

/** Sent (green) and failed (red) as one bar over everything prepared. */
const BatchProgress = ({ counts, size }) => {
  const total = Math.max(counts?.total || 0, 1);
  const sent = counts?.sent || 0;
  const done = sent + (counts?.failed || 0);
  return (
    <Progress
      size={size}
      percent={Math.round((done / total) * 100)}
      success={{ percent: Math.round((sent / total) * 100) }}
      strokeColor="#ff4d4f"
      format={() => `${done} / ${counts?.total || 0}`}
    />
  );
};

// ---------------------------------------------------------------------------

const BatchDetail = ({ batchId, onClose, onChanged }) => {
  const notify = useNotification();
  const [batch, setBatch] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  // Picked once the batch loads: its failures if it has any, else everything.
  const [state, setState] = useState(null);
  const [kind, setKind] = useState(undefined);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [retrying, setRetrying] = useState(false);
  const [resendLeads, setResendLeads] = useState(null);

  const params = useMemo(
    () => ({
      state: state === "all" ? undefined : state,
      failure_kind: kind,
      q: search || undefined,
    }),
    [state, kind, search]
  );

  const loadBatch = useCallback(async () => {
    try {
      const { data } = await api.loadBatch(batchId);
      setBatch(data);
      return data;
    } catch (err) {
      notify("error", "Could not load the batch", errorText(err));
      return null;
    }
  }, [batchId, notify]);

  const loadRows = useCallback(async () => {
    if (!state || state === "skipped") return;
    setLoading(true);
    try {
      const { data } = await api.loadBatchMessages(batchId, {
        ...params,
        page,
        page_size: pageSize,
      });
      setRows(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      notify("error", "Could not load messages", errorText(err));
    }
    setLoading(false);
  }, [batchId, params, page, pageSize, state, notify]);

  useEffect(() => {
    loadBatch().then((data) => {
      if (data) setState((current) => current || (data.counts?.failed ? "failed" : "all"));
    });
  }, [loadBatch]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  // Keep the numbers moving while the batch is.
  useEffect(() => {
    if (!isActive(batch)) return undefined;
    const timer = setInterval(() => {
      loadBatch();
      loadRows();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [batch, loadBatch, loadRows]);

  // A new filter is a new list; old ticks may not be in it.
  useEffect(() => {
    setPage(1);
    setSelected([]);
  }, [state, kind, search]);

  const counts = batch?.counts || {};
  const isWhatsapp = batch?.channel === "WHATSAPP";

  const retry = async (payload, label) => {
    setRetrying(true);
    try {
      const { data } = await api.retryBatch(batchId, payload);
      notify(
        data.retried ? "success" : "info",
        data.retried ? `Retrying ${data.retried} ${label}` : "Nothing to retry",
        data.retried ? "They go out again at the mailbox's pace." : undefined
      );
      setSelected([]);
      setBatch(data.batch);
      loadRows();
      onChanged?.();
    } catch (err) {
      notify("error", "Could not retry", errorText(err));
    }
    setRetrying(false);
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.exportBatch(batchId, params);
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(batch?.name || "batch").replace(/[^\w-]+/g, "_")}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      notify("error", "Could not export", errorText(err));
    }
  };

  /** Window-closed recipients, handed to the composer to send a Meta template. */
  const resendWithTemplate = async () => {
    try {
      const { data } = await api.loadBatchMessages(batchId, {
        state: "failed",
        failure_kind: "WINDOW_CLOSED",
        page_size: 1000,
      });
      const leads = (data.results || []).map((row) => row.record).filter(Boolean);
      if (!leads.length) {
        notify("info", "Nobody to resend to");
        return;
      }
      setResendLeads(leads);
    } catch (err) {
      notify("error", "Could not load recipients", errorText(err));
    }
  };

  const selectedRows = rows.filter((row) => selected.includes(row.id));
  const selectedPermanent = selectedRows.filter((row) => row.failure_kind === "PERMANENT").length;

  const columns = [
    {
      title: "Recipient",
      dataIndex: "record",
      render: (record, row) => (
        <div>
          <div>{record?.name || "—"}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.to_address}
          </Text>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "state",
      width: 150,
      render: (value, row) => {
        const tag = STATE_TAGS[value] || STATE_TAGS.queued;
        return (
          <Space size={4} wrap>
            <Tag color={tag.color} style={{ marginInlineEnd: 0 }}>
              {tag.label}
            </Tag>
            {row.receipt && (
              <Tag color={row.receipt === "read" ? "blue" : "cyan"} style={{ marginInlineEnd: 0 }}>
                {row.receipt === "read" ? "Read" : "Delivered"}
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "Reason",
      dataIndex: "error_message",
      render: (value, row) =>
        value ? (
          <Space direction="vertical" size={2}>
            <KindTag kind={row.failure_kind} />
            <Text style={{ fontSize: 12 }} type={row.state === "failed" ? "danger" : "warning"}>
              {value}
            </Text>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    { title: "Tries", dataIndex: "attempts", width: 70, align: "center" },
    {
      title: "When",
      width: 170,
      render: (_, row) => (
        <Text style={{ fontSize: 12 }}>
          {row.sent_at
            ? when(row.sent_at)
            : row.scheduled_at && row.state === "queued"
              ? `Due ${when(row.scheduled_at)}`
              : when(row.updated_at)}
        </Text>
      ),
    },
  ];

  const skippedColumns = [
    { title: "Record", dataIndex: "name" },
    { title: "Why it was skipped", dataIndex: "reason" },
  ];

  const filters = [
    { value: "failed", label: `Failed (${counts.failed ?? 0})` },
    { value: "retrying", label: `Retrying (${counts.retrying ?? 0})` },
    { value: "queued", label: `Queued (${counts.queued ?? 0})` },
    { value: "sent", label: `Sent (${counts.sent ?? 0})` },
    { value: "all", label: `All (${counts.total ?? 0})` },
    { value: "skipped", label: `Skipped (${counts.skipped ?? 0})` },
  ];

  return (
    <Drawer
      open
      onClose={onClose}
      width="75%"
      title={
        batch ? (
          <Space>
            <ChannelIcon channel={batch.channel} />
            {batch.name}
            <Tag color={BATCH_STATUS[batch.status]?.color}>{BATCH_STATUS[batch.status]?.label}</Tag>
          </Space>
        ) : (
          "Batch"
        )
      }
      extra={
        <Space>
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadBatch();
                loadRows();
              }}
            />
          </Tooltip>
          <Button icon={<DownloadOutlined />} onClick={exportCsv}>
            Export CSV
          </Button>
        </Space>
      }
    >
      {batch && (
        <>
          <Descriptions size="small" column={3} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Template">{batch.template_title || "—"}</Descriptions.Item>
            <Descriptions.Item label="Sent from">{batch.sent_from || "—"}</Descriptions.Item>
            <Descriptions.Item label="By">{batch.created_by_name || "—"}</Descriptions.Item>
            <Descriptions.Item label="Started">{when(batch.created_at)}</Descriptions.Item>
            <Descriptions.Item label="Finished">{when(batch.completed_at)}</Descriptions.Item>
            <Descriptions.Item label="Selected">{counts.selected}</Descriptions.Item>
          </Descriptions>

          <Row gutter={16} style={{ marginBottom: 8 }}>
            <Col span={4}>
              <Statistic title="Sent" value={counts.sent} valueStyle={{ color: "#3f8600" }} />
            </Col>
            {isWhatsapp && (
              <>
                <Col span={3}>
                  <Statistic title="Delivered" value={counts.delivered ?? 0} />
                </Col>
                <Col span={3}>
                  <Statistic title="Read" value={counts.read ?? 0} />
                </Col>
              </>
            )}
            <Col span={isWhatsapp ? 3 : 5}>
              <Statistic title="Queued" value={counts.queued} />
            </Col>
            <Col span={isWhatsapp ? 3 : 5}>
              <Statistic title="Retrying" value={counts.retrying} valueStyle={{ color: "#d48806" }} />
            </Col>
            <Col span={4}>
              <Statistic title="Failed" value={counts.failed} valueStyle={{ color: "#cf1322" }} />
            </Col>
            <Col span={4}>
              <Statistic title="Skipped" value={counts.skipped} />
            </Col>
          </Row>
          <BatchProgress counts={counts} />

          {counts.failed > 0 && (
            <Alert
              style={{ margin: "12px 0" }}
              type="error"
              showIcon
              message={`${counts.failed} failed`}
              description={
                <Space wrap size={[12, 6]}>
                  {["TEMPORARY", "SETUP", "PERMANENT", "WINDOW_CLOSED"]
                    .filter((key) => counts[key.toLowerCase()] > 0)
                    .map((key) => (
                      <a key={key} onClick={() => { setState("failed"); setKind(key); }}>
                        <KindTag kind={key} /> {counts[key.toLowerCase()]}
                      </a>
                    ))}
                </Space>
              }
              action={
                <Space direction="vertical">
                  <Popconfirm
                    title={`Retry ${counts.retryable} failed message${counts.retryable === 1 ? "" : "s"}?`}
                    description="Temporary and setup failures. Bad addresses and closed windows are left out."
                    onConfirm={() => retry({}, "messages")}
                    disabled={!counts.retryable}
                  >
                    <Button
                      type="primary"
                      danger
                      icon={<RedoOutlined />}
                      loading={retrying}
                      disabled={!counts.retryable}
                    >
                      Retry failed ({counts.retryable})
                    </Button>
                  </Popconfirm>
                  {isWhatsapp && counts.window_closed > 0 && (
                    <Button icon={<WhatsAppOutlined />} onClick={resendWithTemplate}>
                      Resend {counts.window_closed} with approved template
                    </Button>
                  )}
                </Space>
              }
            />
          )}
        </>
      )}

      <Flex gap="small" wrap="wrap" align="center" style={{ margin: "12px 0" }}>
        <Segmented options={filters} value={state} onChange={setState} />
        {state !== "skipped" && (
          <>
            <Select
              allowClear
              placeholder="Failure kind"
              style={{ width: 170 }}
              value={kind}
              onChange={setKind}
              options={Object.entries(FAILURE_KINDS).map(([value, info]) => ({
                value,
                label: info.label,
              }))}
            />
            <Input.Search
              allowClear
              placeholder="Name, address or error"
              style={{ width: 260 }}
              onSearch={setSearch}
            />
          </>
        )}
        {selected.length > 0 && (
          <Popconfirm
            title={`Retry ${selected.length} selected?`}
            description={
              selectedPermanent
                ? `${selectedPermanent} have a bad address - only retry them if the lead has been corrected.`
                : undefined
            }
            onConfirm={() => retry({ ids: selected }, "selected")}
          >
            <Button icon={<RedoOutlined />} loading={retrying}>
              Retry selected ({selected.length})
            </Button>
          </Popconfirm>
        )}
      </Flex>

      {state === "skipped" ? (
        <Table
          rowKey="id"
          size="small"
          dataSource={batch?.skipped || []}
          columns={skippedColumns}
          pagination={{ defaultPageSize: 50 }}
          locale={{ emptyText: <Empty description="Nobody was skipped" /> }}
        />
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={rows}
          columns={columns}
          rowSelection={{
            selectedRowKeys: selected,
            preserveSelectedRowKeys: true,
            onChange: setSelected,
            // Only finished failures can be retried; a window-closed one would
            // fail the same way again.
            getCheckboxProps: (row) => ({
              disabled: row.state !== "failed" || row.failure_kind === "WINDOW_CLOSED",
            }),
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextSize) => {
              setPage(nextPage);
              setPageSize(nextSize);
            },
          }}
        />
      )}

      <SendTemplateDrawer
        open={!!resendLeads}
        onClose={() => setResendLeads(null)}
        channel="WHATSAPP"
        leads={resendLeads || []}
        recordType={batch?.record_type || "lead"}
        onSent={onChanged}
      />
    </Drawer>
  );
};

// ---------------------------------------------------------------------------

const MessageBatchesTab = () => {
  const notify = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [channel, setChannel] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const openId = searchParams.get("batch");
  const open = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("batch", id);
    else next.delete("batch");
    setSearchParams(next);
  };

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!quiet) setLoading(true);
      try {
        const { data } = await api.loadBatches({ page, page_size: pageSize, channel });
        setRows(data.results || []);
        setTotal(data.count || 0);
      } catch (err) {
        if (!quiet) notify("error", "Could not load batches", errorText(err));
      }
      if (!quiet) setLoading(false);
    },
    [page, pageSize, channel, notify]
  );

  useEffect(() => {
    load();
  }, [load]);

  const anyActive = rows.some(isActive);
  useEffect(() => {
    if (!anyActive) return undefined;
    const timer = setInterval(() => load({ quiet: true }), POLL_MS);
    return () => clearInterval(timer);
  }, [anyActive, load]);

  const columns = [
    {
      title: "Batch",
      dataIndex: "name",
      render: (value, row) => (
        <Space>
          <ChannelIcon channel={row.channel} />
          <a onClick={() => open(row.id)}>{value}</a>
        </Space>
      ),
    },
    { title: "Template", dataIndex: "template_title" },
    { title: "By", dataIndex: "created_by_name", width: 140 },
    { title: "Started", dataIndex: "created_at", width: 170, render: when },
    {
      title: "Status",
      dataIndex: "status",
      width: 110,
      render: (value) => <Tag color={BATCH_STATUS[value]?.color}>{BATCH_STATUS[value]?.label}</Tag>,
    },
    {
      title: "Progress",
      width: 220,
      render: (_, row) => <BatchProgress counts={row.counts} size="small" />,
    },
    {
      title: "Sent",
      width: 70,
      align: "center",
      render: (_, row) => <Text type="success">{row.counts?.sent ?? 0}</Text>,
    },
    {
      title: "Failed",
      width: 80,
      align: "center",
      render: (_, row) =>
        row.counts?.failed ? (
          <a onClick={() => open(row.id)}>
            <Text type="danger">{row.counts.failed}</Text>
          </a>
        ) : (
          0
        ),
    },
    {
      title: "Skipped",
      width: 80,
      align: "center",
      render: (_, row) => row.counts?.skipped ?? 0,
    },
  ];

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
        <Text type="secondary">
          Every bulk send from the leads or accounts list. Open one to see what failed and retry it.
        </Text>
        <Space>
          <Select
            allowClear
            placeholder="All channels"
            style={{ width: 150 }}
            value={channel}
            onChange={(value) => {
              setChannel(value);
              setPage(1);
            }}
            options={[
              { value: "EMAIL", label: "Email" },
              { value: "WHATSAPP", label: "WhatsApp" },
            ]}
          />
          <Tooltip title="Refresh">
            <Button icon={<ReloadOutlined />} onClick={() => load()} />
          </Tooltip>
        </Space>
      </Flex>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize);
          },
        }}
      />
      {openId && (
        <BatchDetail
          key={openId}
          batchId={openId}
          onClose={() => open(null)}
          onChanged={() => load({ quiet: true })}
        />
      )}
    </>
  );
};

export default MessageBatchesTab;
